import { isLeft } from "fp-ts/Either";
import * as t from "io-ts";
import React from "react";

import {
    ContentStateBlock,
    DraftJsContentState,
    RichTextBlockWithOptions,
} from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";
import { slugify } from "@thelabnyc/thelabui/src/utils/text";

import { ButtonPrimary, ButtonTextPrimary, TextLink } from "../Clickables";
import { headingLevelContext } from "../Headings";
import { Separator } from "../Separator";
import { Svg } from "../Svg";
import { ThemedModule } from "../ThemedModule";
import RichTextRenderer, { EntityComponent } from "./renderer";
import { STYLE_MAP } from "./renderer/defaults";
import { BlockMap } from "./renderer/types";

import styles from "./rich-text.module.scss";

interface Props {
    value: DraftJsContentState;
    dropCap?: boolean;
    className?: string;
    blockOverrides?: BlockMap;
}
const LinkData = t.type({ url: t.string });

const Link = (({ data, children }) => {
    const decoder = LinkData.decode(data);
    if (isLeft(decoder)) return null;
    return (
        <TextLink
            href={decoder.right.url}
            data-track-label={
                typeof children === "string"
                    ? slugify(children)
                    : "richtext_link"
            }
        >
            {children}
        </TextLink>
    );
}) satisfies EntityComponent;

const DocumentLinkData = t.type({ url: t.string, filename: t.string });

const DocumentLink = (({ data, children }) => {
    const decoder = DocumentLinkData.decode(data);
    if (isLeft(decoder)) return null;
    return (
        <TextLink
            href={decoder.right.url}
            data-track-label={
                typeof children === "string"
                    ? slugify(children)
                    : "richtext_document_link"
            }
            download={decoder.right.filename}
        >
            {children}
        </TextLink>
    );
}) satisfies EntityComponent;

const ButtonLink = (({ data, children }) => {
    const decoder = LinkData.decode(data);
    if (isLeft(decoder)) return null;
    return (
        <ButtonPrimary
            href={decoder.right.url}
            data-track-label={
                typeof children === "string"
                    ? slugify(children)
                    : "richtext_link"
            }
        >
            {children}
        </ButtonPrimary>
    );
}) satisfies EntityComponent;

const ButtonTextLink = (({ data, children }) => {
    const decoder = LinkData.decode(data);
    if (isLeft(decoder)) return null;
    return (
        <ButtonTextPrimary
            className={styles.buttonPrimaryText}
            href={decoder.right.url}
            data-track-label={
                typeof children === "string"
                    ? slugify(children)
                    : "richtext_text_link"
            }
        >
            {children}
            <Svg name="arrow" />
        </ButtonTextPrimary>
    );
}) satisfies EntityComponent;

const HEADING_TEXTNUMBERS = [
    "one",
    "two",
    "three",
    "four",
    "five",
    "six",
] as const;
type HeadingMap = Record<
    (typeof HEADING_TEXTNUMBERS)[number],
    keyof React.JSX.IntrinsicElements
>;
const getSemanticHeadingMap = (
    minlevel = 1,
    blocks: ContentStateBlock[],
): HeadingMap => {
    // Starting with the highest heading level allowed by context, assign each
    // block heading style to a semantic level
    const result = {} as HeadingMap;
    for (const n of HEADING_TEXTNUMBERS) {
        if (blocks.some((block) => block.type === `header-${n}`)) {
            result[n] = `h${minlevel}` as keyof React.JSX.IntrinsicElements;
            minlevel += 1;
        } else {
            result[n] = "p";
        }
    }
    return result;
};

/**
 * For most use cases, you want to use this, not RichTextBlock
 */
export function RichText({
    dropCap = false,
    value,
    className,
    blockOverrides,
    ...rest
}: Props & React.ComponentProps<"div">) {
    const { level: startingHeader } = React.useContext(headingLevelContext);
    const headingMap = getSemanticHeadingMap(startingHeader, value.blocks);
    return (
        <div
            {...rest}
            className={concatClassNames([
                className,
                styles.richText,
                dropCap ? styles.dropCap : null,
            ])}
        >
            <RichTextRenderer
                config={{
                    blockMap: {
                        "EYEBROW": {
                            element: "p",
                            props: { className: styles.eyebrow },
                        },
                        "header-one": {
                            element: headingMap.one,
                            props: { className: styles.h1 },
                        },
                        "header-two": {
                            element: headingMap.two,
                            props: { className: styles.h2 },
                        },
                        "header-three": {
                            element: headingMap.three,
                            props: { className: styles.h3 },
                        },
                        "header-four": {
                            element: headingMap.four,
                            props: { className: styles.h4 },
                        },
                        "header-five": {
                            element: headingMap.five,
                            props: { className: styles.h5 },
                        },
                        "header-six": {
                            element: headingMap.six,
                            props: { className: styles.h6 },
                        },
                        "unordered-list-item": {
                            element: "li",
                            props: { className: styles.listItem },
                            wrapper: "ul",
                            wrapperProps: { className: styles.ulist },
                        },
                        "ordered-list-item": {
                            element: "li",
                            props: { className: styles.listItem },
                            wrapper: "ol",
                            wrapperProps: { className: styles.olist },
                        },
                        "unstyled": {
                            element: "p",
                            props: { className: styles.body },
                        },
                        // No props on <li> — footnote items inherit font from the
                        // wrapper .footnote class; browser-default <ol> numbering
                        // is intentional on the public side.
                        "footnote-list-item": {
                            element: "li",
                            wrapper: "ol",
                            wrapperProps: {
                                className: styles.footnote,
                            },
                        },
                        ...blockOverrides,
                    },
                    entityDecorators: {
                        LINK: Link,
                        BUTTONLINK: ButtonLink,
                        SECONDARY_BUTTON_LINK: ButtonTextLink,
                        HORIZONTAL_RULE: () => <Separator />,
                        DOCUMENT: DocumentLink,
                    },
                    styleMap: {
                        ...STYLE_MAP,
                        SUPERSCRIPT: {
                            element: "sup",
                            props: {
                                className: styles.super,
                            },
                        },
                    },
                }}
                data={value}
            />
        </div>
    );
}

/**
 * The way this has panned out is: the actual CMS block will probably only be
 * used for larger blocks of text like blog posts or legalese. So it gets its
 * own styles with that use case in mind
 */
export const RichTextBlock = ({
    value,
}: {
    value: RichTextBlockWithOptions;
}) => {
    if (!value.content) return null;

    return (
        <ThemedModule theme={value.module_theme}>
            <RichText
                className={styles.richTextBlock}
                value={value.content}
                dropCap={!!value.drop_cap}
            />
        </ThemedModule>
    );
};
